import moment, {Moment} from 'moment';
import React from 'react';
import ReactDOM from 'react-dom';

import * as AudioBeep from 'notebook/protocols/viewer/AudioBeep';

import {EditHelpers} from '../timers/TimerConstants';
import {Timer} from '../timers/TimerTypes';
import InlineTimerDisplay from './InlineTimerDisplay';
import TimerDisplay from './TimerDisplay';

export interface TimerComponentProps {
  inline: boolean;
  inlineCopy?: boolean;
  originalMS: number;
  playAudio: boolean;
  startMoment?: Moment | null;
  startMS: number;
  requestChange: (properties: Partial<Timer>) => void;
  allowEdits?: boolean;
  editHelpers?: EditHelpers;
}

class TimerComponent extends React.Component<TimerComponentProps> {
  static defaultProps = {
    inline: false,
    inlineCopy: false,
    playAudio: true,
  };

  interval: NodeJS.Timeout | null | undefined;
  audioRef = React.createRef<AudioBeep.Component>();

  startTimer = (): void => {
    if (this.props.playAudio) {
      AudioBeep.prime(ReactDOM.findDOMNode(this.audioRef.current) as HTMLAudioElement);
    }
    this.props.requestChange({startMoment: moment()});
  };

  startInterval = (): void => {
    this.interval = setInterval(() => {
      if (this.msLeft() === 0) {
        this.stopTimer();
      }
      this.forceUpdate();
    }, 30);
  };

  msLeft = (): number => {
    let msElapsed;
    if (this.props.startMoment) {
      msElapsed = moment().diff(this.props.startMoment);
    } else {
      msElapsed = 0;
    }
    const msLeft = Math.max(0, this.props.startMS - msElapsed);
    return msLeft;
  };

  stopTimer = (): void => {
    this.props.requestChange({
      startMS: this.msLeft(),
      startMoment: null,
    });
  };

  toggleStart = (): void => {
    if (this.msLeft() === 0) {
      this.resetTimer();
      return;
    }
    if (this.props.startMoment) {
      this.stopTimer();
      return;
    }
    this.startTimer();
  };

  resetTimer = (): void => {
    this.props.requestChange({
      startMoment: null,
      startMS: this.props.originalMS,
    });
  };

  override componentDidMount(): void {
    if (this.props.startMoment) {
      this.startInterval();
    }
  }

  override componentWillUnmount(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  override componentDidUpdate(): void {
    if (this.props.playAudio) {
      if (this.msLeft() === 0 && this.props.originalMS > 0) {
        (ReactDOM.findDOMNode(this.audioRef.current) as HTMLAudioElement).play();
      } else {
        (ReactDOM.findDOMNode(this.audioRef.current) as HTMLAudioElement).pause();
      }
    }
    if (this.props.startMoment && !this.interval) {
      this.startInterval();
    } else if (this.interval && !this.props.startMoment) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  override render(): JSX.Element {
    const TimerClass = this.props.inline ? InlineTimerDisplay : TimerDisplay;
    return (
      <span>
        {this.props.playAudio ? <AudioBeep.Component ref={this.audioRef} /> : undefined}
        <TimerClass
          inlineCopy={this.props.inlineCopy}
          inProgress={this.props.startMoment != null}
          msLeft={this.msLeft()}
          msTotal={this.props.originalMS}
          resetTimer={this.resetTimer}
          startTimer={this.startTimer}
          stopTimer={this.stopTimer}
          toggleStart={this.toggleStart}
          allowEdits={this.props.allowEdits}
          editHelpers={this.props.editHelpers}
        />
      </span>
    );
  }
}

export default TimerComponent;
